/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { Attachment } from '@src/app/model/attachment';
import { BankAccount } from '@src/app/model/bankAccount';
import { BCode } from '@src/app/model/bcode';
import { Cycle } from '@src/app/model/cycle';
import { MailItem } from '@src/app/model/mailMerge';
import { Period } from '@src/app/model/period';
import { Person } from '@src/app/model/person';
import { Schedule } from '@src/app/model/schedule';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { Supplier } from '@src/app/model/supplier';
import { Unit } from '@src/app/model/unit';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { Field } from '@src/app/shared/field/Field';

class TxnType extends AbstractObject {
    code: string;
    color?: string;
}

/** One work order this invoice settles, and whether raising it accepts the work order too. */
export interface RequestLink {
    requestId: uuid;
    accept?: boolean;
}
export class Txn extends AbstractObject {

    bCodeId: uuid;
    bCode?: BCode;
    scheduleId?: uuid;
    schedule?: Schedule;
    personId?: uuid;
    person?: Person;
    refNr?: number;

    typeId: uuid;
    type?: TxnType;

    txnDate: string;
    ledgerId: uuid;
    ledger?: AbstractObject;
    txnCycleId: uuid;
    txnCycle?: Cycle;
    txnPeriodId: uuid;
    txnPeriod?: Period;

    approvedAt?: number; // date
    approvedBy?: number; // user id
    approverName?: string;

    requestLinks?: RequestLink[];
    requests?: ServiceRequest[] = [];

    supplierId?: uuid;
    supplier?: Supplier;
    reference?: string;

    svcFrom?: string;
    svcTo?: string;

    bankAccountId?: uuid;
    bankAccount?: BankAccount;
    bankItemId?: uuid;

    debit?: number = 0;
    credit?: number = 0;
    outstanding?: number = 0;
    unallocated?: number = 0; // Not stored
    balance?: number; //not stored
    allocate?: number; //not stored

    details?: Txn[] = [];

    parent?: Txn;
    parentId?: uuid;
    parentRefNr?: number;
    parentTypeId?: uuid;

    relations?: Txn[] = [];
    relatedId?: uuid;
    relatedRev?: number; // Not stored directly, used to ensure an update to related transaction updates correct revision.
    related?: Txn;

    lineItems?: Txn[] = []; // Not Stored merged with details on purchase invoices
    journalItems?: Txn[] = []; // Carried on lineItems to server to record relation journals.
    allocations?: Txn[] = []; // Taken out from details in beforeEdit

    // Not stored, calculated total of journal entries
    totalDebit?: number;
    totalCredit?: number;

    vatCode?: string;
    vatRate?: number;
    vatAmount?: number;

    unitId?: uuid;
    unit?: Unit;
    unitName?: string; //sometimes available from join
    associatedUnits?: string; //calc on bankIn

    attachments?: Attachment[] = [];
    availableAttachments?: Attachment[] = []; // Not Stored...
    mailItems?: MailItem[] = [];
    ownerChangeId?: uuid;

    constructor(o: Partial<Txn> = {}) {
        super(o);
    }

    /*
    * Remember types are duplicated in server/app/Models/
    */

    static readonly TYPE = {
        JRNL: { 'id': 1, 'name': 'Journal', code: 'jrnl' } as TxnType,
        JRNL_DETAIL: { 'id': 2, 'name': 'Journal Detail', code: 'jrnl_dtl' } as TxnType,
        YEAREND_DETAIL: { 'id': 3, 'name': 'Balance Forward', code: 'yend_dtl' } as TxnType,
        SURPLUS: { 'id': 4, 'name': 'Prev Surplus/Deficit', code: 'surplus'} as TxnType,

        INVOICE: { 'id': 10, 'name': $localize`Service Charge`, code: 'inv' } as TxnType,
        INVOICE_ITEM: { 'id': 11, 'name': $localize`Service Charge Item`, code: 'inv_item' } as TxnType,
        CREDIT_NOTE: { 'id': 12, 'name': $localize`Svc Credit Note`, code: 'cn' } as TxnType,
        CREDIT_NOTE_ITEM: { 'id': 13, 'name': $localize`Svc Credit Detail`, code: 'cn_item' } as TxnType,
        REVERSAL: { 'id': 14, 'name': $localize`Payment Reversal`, code: 'reverse' } as TxnType,
        REVERSAL_DTL: { 'id': 15, 'name': $localize`Reversal Detail`, code: 'rev_dtl' } as TxnType,
        REVERSAL_FEE: { 'id': 16, 'name': $localize`Payment Reversal Fee`, code: 'rev_fee' } as TxnType,
        REV_FEE_DTL: { 'id': 17, 'name': $localize`Reversal Fee Detail`, code: 'rev_feed' } as TxnType,

        CHARGES_VAT: { 'id': 19, 'name': $localize`Charges VAT`, code: 'inv_vat' } as TxnType,


        PURCHASE: { 'id': 40, 'name': $localize`Purchase Invoice`, code: 'pur' } as TxnType,
        PURCHASE_ITEM: { 'id': 41, 'name': $localize`Purchase Inv. Dtl`, code: 'pur_item' } as TxnType,

        PURCHASE_CN: { 'id': 42, 'name': $localize`Purchase Credit Note`, code: 'pcn', color: 'red' } as TxnType,
        PURCHASE_CN_ITEM: { 'id': 43, 'name': $localize`Purchase Credit Dtl`, code: 'pcn_item', color: 'red'  } as TxnType,
        PURCHASE_CN_USED: {'id': 44, 'name': $localize`AP Credit Used`, code:'pcn_used'} as TxnType,
        PURCHASE_CN_APPLY: { 'id': 45, 'name': $localize`AP Credit Apply`, code: 'pcn_apply' } as TxnType,

        PURCHASE_VAT: { 'id': 46, 'name': $localize`Purchases VAT`, code: 'pur_vat' } as TxnType,

        CASH_PURCHASE: { 'id': 48, 'name': $localize`Cash Purchase`, code: 'cpur' } as TxnType,
        CASH_PURCHASE_ITEM: { 'id': 49, 'name': $localize`Cash Purchase Detail`, code: 'cpur_item' } as TxnType,        

        PREPAY_REV_EXP: { 'id': 50, 'name': 'Reverse Expense', code: 'jrnl' } as TxnType,
        PREPAY_ASSET: { 'id': 51, 'name': 'Prepay Asset', code: 'jrnl' } as TxnType,
        PREPAY_WRITEDOWN: { 'id': 52, 'name': 'Writedown Prepayment', code: 'jrnl' } as TxnType,
        PREPAY_CHARGE: { 'id': 53, 'name': 'Prepaid Expense Charge', code: 'jrnl' } as TxnType,

        PURCHASE_ACCRUAL: { 'id': 54, 'name': 'Purchase Accrual', code: 'puracc', color: 'blue' } as TxnType,
        PURCHASE_ACCRUAL_ITEM: { 'id': 55, 'name': 'Accrual Item', code: 'puracc_itm', color: 'blue' } as TxnType,
        PURCHASE_ACCRUAL_REV: { 'id': 56, 'name': 'Accrual Reversal', code: 'puracc_rev' } as TxnType,

        PURCHASE_CN_ACCRUAL: { 'id': 57, 'name': 'AP Credit Accrual', code: 'pcnacc', color: 'deeppink' } as TxnType,
        PURCHASE_CN_ACCRUAL_ITEM: { 'id': 58, 'name': 'Accr Credit Item', code: 'pcnacc_itm', color: 'deeppink' } as TxnType,
        PURCHASE_CN_ACCRUAL_REV: { 'id': 59, 'name': 'Accr Credit Reversal', code: 'pcnacc_rev' } as TxnType,

        BANK_IN: { 'id': 70, 'name': $localize`Bank Receipt`, code: 'bank_in' } as TxnType,
        BANK_IN_ALLOC: { 'id': 71, 'name': $localize`Receipt Allocation`, code: 'rec_alloc' } as TxnType,
        BANK_IN_ON_ACCT: { 'id': 72, 'name': $localize`Receipt On Acct`, code: 'rec_acct' } as TxnType,
        BANK_IN_DISCOUNT: { 'id': 73, 'name': $localize`Settlement Discount`, code: 'rec_disc' } as TxnType,

        BANK_OUT: { 'id': 75, 'name': $localize`Bank Payment`, code: 'bank_out' } as TxnType,
        BANK_OUT_ALLOC: { 'id': 76, 'name': $localize`Payment Allocation`, code: 'pay_alloc' } as TxnType,
        BANK_CHARGE: { 'id': 77, 'name': $localize`Bank Charge`, code: 'bank_chg' } as TxnType,
        BANK_CHG_DTL: { 'id': 78, 'name': $localize`Bank Charge Detail`, code: 'bank_chgd' } as TxnType,

        BANK_TRAN: { 'id': 79, 'name' : $localize `Bank Transfer`, code: 'bank_tran'} as TxnType,
        BANK_TRAN_IN: { 'id': 80, 'name': $localize`Transfer In`, code: 'bank_tr_in' } as TxnType,

        BANK_INT_RECD: { 'id': 81, 'name': $localize`Bank Interest Recd`, code: 'bank_int' } as TxnType,
        BANK_INT_INCOME: { 'id': 82, 'name': $localize`Bank Interest Income`, code: 'bank_intd' } as TxnType,
    }

    static readonly TYPES : TxnType[] = [
        Txn.TYPE.JRNL,
        Txn.TYPE.JRNL_DETAIL,
        Txn.TYPE.YEAREND_DETAIL,
        Txn.TYPE.SURPLUS,

        Txn.TYPE.INVOICE,
        Txn.TYPE.INVOICE_ITEM,
        Txn.TYPE.CREDIT_NOTE,
        Txn.TYPE.CREDIT_NOTE_ITEM,
        Txn.TYPE.REVERSAL,
        Txn.TYPE.REVERSAL_DTL,
        Txn.TYPE.REVERSAL_FEE,
        Txn.TYPE.REV_FEE_DTL,
        Txn.TYPE.CHARGES_VAT,

        Txn.TYPE.PURCHASE,
        Txn.TYPE.PURCHASE_ITEM,
        Txn.TYPE.PURCHASE_CN,
        Txn.TYPE.PURCHASE_CN_ITEM,
        Txn.TYPE.PURCHASE_CN_USED,
        Txn.TYPE.PURCHASE_CN_APPLY,
        Txn.TYPE.PURCHASE_VAT,

        Txn.TYPE.PREPAY_REV_EXP,
        Txn.TYPE.PREPAY_ASSET,
        Txn.TYPE.PREPAY_WRITEDOWN,
        Txn.TYPE.PREPAY_CHARGE,

        Txn.TYPE.CASH_PURCHASE,
        Txn.TYPE.CASH_PURCHASE_ITEM,

        Txn.TYPE.PURCHASE_ACCRUAL,
        Txn.TYPE.PURCHASE_ACCRUAL_ITEM,
        Txn.TYPE.PURCHASE_ACCRUAL_REV,

        Txn.TYPE.PURCHASE_CN_ACCRUAL,
        Txn.TYPE.PURCHASE_CN_ACCRUAL_ITEM,
        Txn.TYPE.PURCHASE_CN_ACCRUAL_REV,

        Txn.TYPE.BANK_IN,
        Txn.TYPE.BANK_IN_ALLOC,
        Txn.TYPE.BANK_IN_ON_ACCT,
        Txn.TYPE.BANK_IN_DISCOUNT,
        Txn.TYPE.BANK_OUT,
        Txn.TYPE.BANK_OUT_ALLOC,
        Txn.TYPE.BANK_CHARGE,
        Txn.TYPE.BANK_CHG_DTL,
        Txn.TYPE.BANK_TRAN,
        Txn.TYPE.BANK_TRAN_IN,
        Txn.TYPE.BANK_INT_RECD,
        Txn.TYPE.BANK_INT_INCOME,
    ];

    static readonly LEDGER = {
        GL: { 'id': 99, 'name': $localize`Journals`, code: 'gl' },
        AR: { 'id': 1, 'name': $localize`Service Charge Accounting`, code: 'ar' },
        AP: { 'id': 4, 'name': $localize`Payables`, code: 'ap' },
        BL: { 'id': 7, 'name': $localize`Bank`, code: 'bl' },
        AA: { 'id': 9, 'name': $localize`Assets`, code: `aa` },
    }

    static readonly LEDGERS = [
        Txn.LEDGER.GL,
        Txn.LEDGER.AR,
        Txn.LEDGER.AP,
        Txn.LEDGER.BL,
        Txn.LEDGER.AA
    ];

    static readonly MONTHS: AbstractObject[] = [
        { id: 1, name: $localize`January` }, { id: 2, name: $localize`February` },
        { id: 3, name: $localize`March` }, { id: 4, name: $localize`April` },
        { id: 5, name: $localize`May` }, { id: 6, name: $localize`June` },
        { id: 7, name: $localize`July` }, { id: 8, name: $localize`August` },
        { id: 9, name: $localize`September` }, { id: 10, name: $localize`October` },
        { id: 11, name: $localize`November` }, { id: 12, name: $localize`December` },
    ];

    /**
     * Calendar month of the transaction, for charting monies by month. A fixed list rather than a
     * self-populating one, so the id sorts the bars and the name labels them with no column behind
     * it - the page holding this field needs a `month` customFilter to make a bar click filter.
     */
    static makeMonthField() {
        return FormPicklistComponent.make('Month', 'month', null,
            { items: Txn.MONTHS },
            { visible: Field.noShow, sendServer: false, calculateValue: (o: Txn) => +o.txnDate?.substring(5, 7) }
        );
    }

    /**
     * Need a picklist on year for charts to work
     * Field calculates the year from transaction date
     * And creates a picklist of 12 years back from today.
     */
    static makeYearField() {
        const thisYear = new Date().getFullYear();
        const items = Array.from({ length: 12 },
            (_, i) => ({ id: thisYear - i, name: `${thisYear - i}` } as AbstractObject));

        return FormPicklistComponent.make('Year', 'year', null,
            { items },
            { visible: Field.noShow, sendServer: false, calculateValue: (o: Txn) => +o.txnDate?.substring(0, 4) }
        );
    }

    static getType(id: uuid) {
        return Txn.TYPES.find(type => type.id === id);
    }

    /** Columns for picking a sample invoice, e.g. for the comms-template Billing type Test/Preview. */
    static pickDialogFormConfig(): FormConfig {
        return new FormConfig({
            fieldSet: new FieldSet({
                fields: [
                    FormNumberComponent.make('Ref', 'refNr', { format: 'plain' }),
                    FormTextComponent.make('Contact', 'personFullName', { calculateValue: (o: Txn) => o.person?.fullName }),
                    FormTextComponent.make('Unit', 'unit.name'),
                    FormDateComponent.make('Date', 'txnDate'),
                    FormNumberComponent.makeCurrency('Total', 'debit'),
                    FormNumberComponent.makeCurrency('Outstanding', 'outstanding'),
                ],
                formLayout: LAYOUT_OPTIONS.singleCol,
            }),
            title: $localize`Invoices`,
            mode: 'list',
        });
    }

    static DELETE_MSG = $localize`
        Cannot truly delete transaction for audit reasons.
        If you delete transaction it will still be visible,
        but it's value is reduced to zero so overall numbers are not effected.
        If you wish to continue, enter a reason and press the delete option`;

    static getTxnLink(o: Txn) {
        // This should probably be decommissioned in favour of getTxnLink on FormNavLinkComponent, but that is another days work
        if (o) {
            if (o.typeId === Txn.TYPE.INVOICE.id) {
                return `/${o.teamId}/${Txn.TYPE.INVOICE.code}/${o.id}`
            } else if (o.typeId === Txn.TYPE.INVOICE_ITEM.id) {
                return `/${o.teamId}/${Txn.TYPE.INVOICE.code}/${o.parentId}`
            } else if (o.typeId === Txn.TYPE.CHARGES_VAT.id) {
                if (o.debit > 0) {
                    return `/${o.teamId}/${Txn.TYPE.CREDIT_NOTE.code}/${o.parentId}`
                } else {
                    return `/${o.teamId}/${Txn.TYPE.INVOICE.code}/${o.parentId}`
                }
            } else if (o.typeId === Txn.TYPE.BANK_IN.id) {
                console.log('Bank Receipt Link', o);
                return `/${o.teamId}/${Txn.TYPE.BANK_IN.code}/${o.id}`
            } else if (o.typeId === Txn.TYPE.BANK_IN_ALLOC.id || o.typeId === Txn.TYPE.BANK_IN_ON_ACCT.id || o.typeId === Txn.TYPE.BANK_IN_DISCOUNT.id) {
                return `/${o.teamId}/${Txn.TYPE.BANK_IN.code}/${o.parentId}`

            } else if (o.typeId === Txn.TYPE.JRNL.id) {
                return `/${o.teamId}/${Txn.TYPE.JRNL.code}/${o.id}`
            } else if ([
                Txn.TYPE.JRNL_DETAIL.id,
                Txn.TYPE.YEAREND_DETAIL.id,
                Txn.TYPE.SURPLUS.id
            ].indexOf(+o.typeId) >= 0) {
                return `/${o.teamId}/${Txn.TYPE.JRNL.code}/${o.parentId}`

            } else if ([
                Txn.TYPE.BANK_CHARGE.id,
                Txn.TYPE.BANK_TRAN.id,
                Txn.TYPE.BANK_INT_RECD.id
            ].indexOf(+o.typeId) >= 0) {
                return `/${o.teamId}/${Txn.LEDGER.BL.code}/${o.id}`
            } else if ([
                Txn.TYPE.BANK_CHG_DTL.id,
                Txn.TYPE.BANK_TRAN_IN.id,
                Txn.TYPE.BANK_INT_INCOME.id
            ].indexOf(+o.typeId) >= 0) {
                return `/${o.teamId}/${Txn.LEDGER.BL.code}/${o.parentId}`

            } else if (o.typeId === Txn.TYPE.CREDIT_NOTE.id) {
                return `/${o.teamId}/${Txn.TYPE.CREDIT_NOTE.code}/${o.id}`
            } else if (o.typeId === Txn.TYPE.CREDIT_NOTE_ITEM.id) {
                return `/${o.teamId}/${Txn.TYPE.CREDIT_NOTE.code}/${o.parentId}`
            } else if ([
                Txn.TYPE.PURCHASE.id, 
                Txn.TYPE.CASH_PURCHASE.id,
                Txn.TYPE.PURCHASE_CN.id,
                Txn.TYPE.PURCHASE_ACCRUAL.id,
                Txn.TYPE.PURCHASE_CN_ACCRUAL.id
            ].indexOf(o.typeId as number) >= 0) {
                return `/${o.teamId}/${Txn.TYPE.PURCHASE.code}/${o.id}`
            } else if ([
                Txn.TYPE.PURCHASE_ITEM.id,
                Txn.TYPE.CASH_PURCHASE_ITEM.id,
                Txn.TYPE.PURCHASE_CN_ITEM.id,
                Txn.TYPE.PURCHASE_CN_APPLY.id,
                Txn.TYPE.PURCHASE_CN_USED.id,
                Txn.TYPE.PURCHASE_VAT,
                Txn.TYPE.PREPAY_WRITEDOWN.id,
                Txn.TYPE.PREPAY_CHARGE.id,
                Txn.TYPE.PREPAY_ASSET.id,
                Txn.TYPE.PREPAY_REV_EXP.id,
                Txn.TYPE.PURCHASE_ACCRUAL_ITEM.id,
                Txn.TYPE.PURCHASE_ACCRUAL_REV.id,
                Txn.TYPE.PURCHASE_CN_ACCRUAL_ITEM.id,
                Txn.TYPE.PURCHASE_CN_ACCRUAL_REV.id,
                Txn.TYPE.PURCHASE_VAT.id
            ].indexOf(+o.typeId) >= 0) {
                return `/${o.teamId}/${Txn.TYPE.PURCHASE.code}/${o.parentId}`

            } else if (o.typeId === Txn.TYPE.BANK_OUT_ALLOC.id) {
                return `/${o.teamId}/${Txn.TYPE.BANK_OUT.code}/${o.parentId}`
            } else if (o.typeId === Txn.TYPE.BANK_OUT.id) {
                return `/${o.teamId}/${Txn.TYPE.BANK_OUT.code}/${o.id}`
            } else {
                console.warn('dunnohowtolinkto ' + o.typeId, o);
                return '/dunnohowtolinkto';
            }
        } else {
            return 'onHeader';
        }
    }
}
